import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { useMemo } from "react";

import { t } from "@/i18n-js/instance";
import { ErrorTemplate } from "@/react/components/shared/ErrorPages/ErrorTemplate";

import { Checkout } from "../../Checkout";
import { CheckoutPage } from "../../Checkout/CheckoutPage";
import { useCheckoutSession } from "../../Checkout/contexts/CheckoutSessionContext";
import type { CheckoutProps } from "../../Checkout/types/CheckoutProps";
import {
  getStepById,
  getMainStep,
  getMainOffer,
} from "../helpers/SessionStepQueryHelper";

interface MainOfferPageProps {
  stepId?: number;
}

export const MainOfferPage = ({ stepId }: MainOfferPageProps) => {
  const { session, justCompletedStepIds } = useCheckoutSession();
  const { isLoading: isCommunityLoading } = usePunditUserContext();

  const step = stepId
    ? getStepById(session?.steps || [], stepId)
    : getMainStep(session?.steps || []);

  const sessionStepId = step?.id;
  const mainOffer = getMainOffer(step);

  const hasJustCompletedThisStep =
    !!sessionStepId && justCompletedStepIds.has(sessionStepId);

  const checkoutProps: CheckoutProps | null = useMemo(() => {
    if (!session || !mainOffer) {
      return null;
    }

    return {
      paywall: mainOffer.paywall,
      currentCommunity: {
        id: session.community.id,
        name: session.community.name,
        icon_url: session.community.icon_url,
        logo_url: session.community.logo_url,
        terms_url: session.community.terms_url,
        privacy_url: session.community.privacy_url,
      },
      currentUser: session.user
        ? {
            id: session.user.id,
            name: session.user.name,
            first_name: session.user.first_name,
            last_name: session.user.last_name,
            email: session.user.email,
            avatar_url: session.user.avatar_url,
          }
        : undefined,
      currentCommunityMember: session.community_member,
      currentCommunityMemberPaymentProcessorSessionClientSecret:
        mainOffer.paywall.member_payment_processor?.session_client_secret ??
        undefined,
      isMemberWithSavedPaymentMethods:
        mainOffer.paywall.member_payment_processor
          ?.is_member_with_saved_payment_methods || false,
      currentCommunityProcessorPaymentMethodConfigurationId:
        session.community.payment_processor.payment_method_configuration_id ||
        "",
      isLinkPreferenceEnabled:
        session.community.payment_processor.is_link_preference_enabled,
      hasAlreadyPurchased:
        mainOffer.paywall.policies.has_already_purchased &&
        !hasJustCompletedThisStep,
      loginUrl: session.settings.login_url,
      resetPasswordUrl: session.settings.reset_password_url,
      communityRootUrl: session.settings.community_root_url,
      checkoutConfirmationUrl: session.settings.checkout_confirmation_url,
      processorId: session.community.payment_processor.processor_id,
      processorKey: session.community.payment_processor.processor_key,
      areCouponsEnabled: mainOffer.paywall.policies.are_coupons_enabled,
      isMemberAddressComplete: session.policies.is_member_address_complete,
      isMemberAddressRequired: session.policies.is_member_address_required,
      memberBillingInfo: session.community_member?.billing_info,
      isBrazilianAccountAndProcessorStripeBr:
        session.community.payment_processor
          .is_brazilian_account_and_processor_stripe_br,
      isTaxCollectionEnabled:
        session.community.payment_processor.is_tax_collection_enabled,
      shouldShowTaxIdForm: session.policies.should_show_tax_id_form,
      isTaxIdMandatory: mainOffer.paywall.policies.is_tax_id_mandatory,
      authProviderProperties: {
        name: session.auth_provider?.name,
        is_sso_authentication_required:
          session.auth_provider?.is_sso_authentication_required,
      },
      sessionStepId,
    };
  }, [session, mainOffer, sessionStepId, hasJustCompletedThisStep]);

  if (!checkoutProps || isCommunityLoading) {
    return <CheckoutPage.Skeleton />;
  }

  if (!mainOffer) {
    return (
      <ErrorTemplate
        errorCode={404}
        title={t("paywall_checkout.error_boundary.title")}
      />
    );
  }

  return <Checkout {...checkoutProps} />;
};
